/**
 * SessionTimeoutWarning — Modal overlay warning user session is expiring
 * Tech-Noir aesthetic with copper accents and countdown timer
 */
import { memo } from "react";
import { TYPE, SP, RADIUS, TRANSITION, GLASS, Z, type ThemeObject } from "../shared/theme.js";

interface SessionTimeoutWarningProps {
  showWarning: boolean;
  remainingSeconds: number;
  onContinue: () => void;
  onLogout: () => void;
  T: ThemeObject;
}

const SessionTimeoutWarning = memo(
  ({ showWarning, remainingSeconds, onContinue, onLogout, T }: SessionTimeoutWarningProps) => {
    if (!showWarning) return null;

    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    const timeDisplay = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;

    // Color indicator: green > yellow > red based on remaining time
    let indicatorColor = T.success;
    if (remainingSeconds < 30) indicatorColor = T.danger;
    else if (remainingSeconds < 60) indicatorColor = T.warning;

    const glassBg = GLASS.dark;

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0, 0, 0, 0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: Z.modal,
          padding: SP[4],
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          animation: "fadeIn 0.2s ease-in-out",
        }}
      >
        <div
          style={{
            background: glassBg.bg,
            backdropFilter: glassBg.blur,
            WebkitBackdropFilter: glassBg.blur,
            border: `1px solid ${glassBg.border}`,
            borderRadius: RADIUS.lg,
            padding: SP[6],
            maxWidth: 420,
            textAlign: "center",
            animation: "slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
          }}
        >
          {/* Icon + Title */}
          <div
            style={{
              fontSize: 48,
              marginBottom: SP[3],
            }}
          >
            ⏱️
          </div>

          <h2
            style={{
              fontSize: TYPE.lg,
              fontWeight: TYPE.bold,
              color: T.text,
              margin: 0,
              marginBottom: SP[2],
              fontFamily: TYPE.fontSans,
            }}
          >
            Sua sessão vai expirar
          </h2>

          {/* Description */}
          <p
            style={{
              fontSize: TYPE.base,
              color: T.sub,
              margin: 0,
              marginBottom: SP[5],
              lineHeight: 1.6,
              fontFamily: TYPE.fontSans,
            }}
          >
            Por segurança, você será desconectado em breve por inatividade.
          </p>

          {/* Countdown Timer */}
          <div
            style={{
              background: `rgba(${
                indicatorColor === T.danger
                  ? "220, 38, 38"
                  : indicatorColor === T.warning
                    ? "217, 119, 6"
                    : "22, 163, 74"
              }, 0.1)`,
              border: `2px solid ${indicatorColor}`,
              borderRadius: RADIUS.md,
              padding: SP[4],
              marginBottom: SP[5],
              transition: TRANSITION.fast,
            }}
          >
            <div
              style={{
                fontSize: TYPE["3xl"],
                fontWeight: TYPE.heavy,
                fontFamily: TYPE.fontMono,
                color: indicatorColor,
                letterSpacing: 2,
              }}
            >
              {timeDisplay}
            </div>
          </div>

          {/* Actions */}
          <div
            style={{
              display: "flex",
              gap: SP[3],
              flexDirection: "column",
            }}
          >
            <button
              onClick={onContinue}
              style={{
                padding: `${SP[2]} ${SP[5]}`,
                background: T.accent,
                color: T.accentText,
                border: "none",
                borderRadius: RADIUS.md,
                fontSize: TYPE.sm,
                fontWeight: TYPE.semibold,
                cursor: "pointer",
                transition: TRANSITION.fast,
                fontFamily: TYPE.fontSans,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.opacity = "0.85";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.opacity = "1";
              }}
            >
              ✓ Continuar Conectado
            </button>

            <button
              onClick={onLogout}
              style={{
                padding: `${SP[2]} ${SP[5]}`,
                background: "transparent",
                color: T.danger,
                border: `1.5px solid ${T.danger}`,
                borderRadius: RADIUS.md,
                fontSize: TYPE.sm,
                fontWeight: TYPE.semibold,
                cursor: "pointer",
                transition: TRANSITION.fast,
                fontFamily: TYPE.fontSans,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = `${T.danger}15`;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = "transparent";
              }}
            >
              ✕ Sair Agora
            </button>
          </div>
        </div>

        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(16px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </div>
    );
  }
);

SessionTimeoutWarning.displayName = "SessionTimeoutWarning";

export default SessionTimeoutWarning;
